/**
 * Pure helpers for the harper.js spell & grammar check.
 * Kept free of harper/DOM dependencies so they can be unit tested.
 */

// Mirrors harper.js `SuggestionKind`
export const SUGGESTION_KIND = {
  REPLACE: 0,
  REMOVE: 1,
  INSERT_AFTER: 2
};

// Lint kinds rendered as spelling errors (red); every other kind is grammar (blue)
const SPELLING_KINDS = ['Spelling', 'Typo'];

export const isSpellingLint = lint => SPELLING_KINDS.includes(lint.kind);

// Menu headings for harper's lint kinds, worded like Word's proofing categories
const KIND_LABELS = {
  Spelling: 'Spelling',
  Typo: 'Spelling',
  Agreement: 'Subject-Verb Agreement',
  BoundaryError: 'Word Spacing',
  Capitalization: 'Capitalization',
  Eggcorn: 'Word Choice',
  Malapropism: 'Word Choice',
  WordChoice: 'Word Choice',
  Usage: 'Word Choice',
  Formatting: 'Spacing',
  Punctuation: 'Punctuation',
  Redundancy: 'Conciseness',
  Repetition: 'Repeated Word',
  WordOrder: 'Word Order'
};

export const getKindLabel = lint => KIND_LABELS[lint.kind] || 'Grammar';

// Harper messages that are unclear to non-linguists
const MESSAGE_OVERRIDES = {
  'French spaces are generally not recommended.':
    'Use a single space between sentences.'
};

export const getLintMessage = lint =>
  MESSAGE_OVERRIDES[lint.message] || lint.message;

/**
 * Clamps lints to the text, sorts them and drops empty or overlapping ones.
 * Harper spans are JS string (UTF-16) indices, so they can be used with `slice` as-is.
 */
export const normalizeLints = (text, lints) => {
  let lastEnd = -1;

  return lints
    .map(lint => ({
      ...lint,
      start: Math.max(0, Math.min(lint.start, text.length)),
      end: Math.max(0, Math.min(lint.end, text.length))
    }))
    .sort((a, b) => a.start - b.start || b.end - a.end)
    .filter(lint => {
      const keep = lint.end > lint.start && lint.start >= lastEnd;
      if (keep) {
        lastEnd = lint.end;
      }
      return keep;
    });
};

/**
 * Keeps existing lints aligned while the text is being edited and before it is re-linted:
 * lints after the edit are shifted, lints touched by the edit are dropped.
 */
export const adjustLintsForEdit = (prevText, nextText, lints) => {
  const maxPrefix = Math.min(prevText.length, nextText.length);
  let prefix = 0;
  while (prefix < maxPrefix && prevText[prefix] === nextText[prefix]) {
    prefix += 1;
  }
  let suffix = 0;
  while (
    suffix < maxPrefix - prefix &&
    prevText[prevText.length - 1 - suffix] ===
      nextText[nextText.length - 1 - suffix]
  ) {
    suffix += 1;
  }
  const editEnd = prevText.length - suffix;
  const delta = nextText.length - prevText.length;

  return lints.reduce((adjusted, lint) => {
    if (lint.end < prefix) {
      adjusted.push(lint);
    } else if (lint.start > editEnd) {
      adjusted.push({
        ...lint,
        start: lint.start + delta,
        end: lint.end + delta
      });
    }
    return adjusted;
  }, []);
};

/**
 * Splits text into plain and flagged segments for rendering underlines
 * @returns {Array<{ text: string, lintIndex: ?number }>}
 */
export const buildSegments = (text, lints) => {
  const segments = [];
  let cursor = 0;

  lints.forEach((lint, lintIndex) => {
    if (lint.start > cursor) {
      segments.push({ text: text.slice(cursor, lint.start), lintIndex: null });
    }
    segments.push({ text: text.slice(lint.start, lint.end), lintIndex });
    cursor = lint.end;
  });

  if (cursor < text.length) {
    segments.push({ text: text.slice(cursor), lintIndex: null });
  }

  return segments;
};

/**
 * Applies a suggestion to the text
 * @returns {{ text: string, caret: number }} the new text and caret position after the edit
 */
export const applySuggestion = (text, lint, suggestion) => {
  const before = text.slice(0, lint.start);
  const after = text.slice(lint.end);
  const problem = text.slice(lint.start, lint.end);

  switch (suggestion.kind) {
    case SUGGESTION_KIND.REMOVE:
      return { text: before + after, caret: lint.start };
    case SUGGESTION_KIND.INSERT_AFTER:
      return {
        text: before + problem + suggestion.text + after,
        caret: lint.end + suggestion.text.length
      };
    default:
      return {
        text: before + suggestion.text + after,
        caret: lint.start + suggestion.text.length
      };
  }
};

const describeWhitespace = text =>
  text === ' ' ? 'a single space' : `"${text.replace(/\n/g, '↵')}"`;

export const getSuggestionLabel = (lint, suggestion) => {
  switch (suggestion.kind) {
    case SUGGESTION_KIND.REMOVE:
      return lint.problemText.trim()
        ? `Remove "${lint.problemText}"`
        : 'Remove the extra space';
    case SUGGESTION_KIND.INSERT_AFTER:
      return `Insert "${suggestion.text}"`;
    default:
      return suggestion.text.trim()
        ? suggestion.text
        : `Use ${describeWhitespace(suggestion.text)}`;
  }
};

// Identifies a lint for "Ignore All", independent of where it occurs in the text
export const getIgnoreKey = lint =>
  `${lint.kind}|${lint.message}|${lint.problemText.toLowerCase()}`;
